import React from 'react';
import {
  CustomerLite,
  Site,
  formatCustomerLabel,
  getCustomerInputDisplayValue,
  handleInteractiveKeyDown,
} from './createPlantHelpers';

const dropdownArrowStyle = (isOpen: boolean): React.CSSProperties => ({
  position: 'absolute',
  right: '14px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: isOpen ? '#60bfda' : '#94a3b8',
  fontSize: '14px',
  pointerEvents: 'none',
});

const dropdownListStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% - 1px)',
  left: 0,
  marginTop: '0',
  backgroundColor: '#0f172a',
  border: '1px solid #1e293b',
  borderTop: '1px solid #1e293b',
  borderRadius: '0 0 10px 10px',
  padding: '8px 0',
  width: '100%',
  maxHeight: '300px',
  overflowY: 'auto',
  zIndex: 1000,
  boxShadow: '0 18px 36px -14px rgba(15, 23, 42, 0.85)',
};

const DropdownChevron: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5"
      stroke="#FCFCFD"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface SearchableInputStyleOptions {
  isOpen: boolean;
}

function getSearchableInputStyle({ isOpen }: SearchableInputStyleOptions): React.CSSProperties {
  return {
    paddingRight: '44px',
    cursor: isOpen ? 'text' : 'pointer',
    borderBottomLeftRadius: isOpen ? '0' : undefined,
    borderBottomRightRadius: isOpen ? '0' : undefined,
    borderBottom: isOpen ? 'none' : undefined,
    marginBottom: isOpen ? '0' : undefined,
    zIndex: isOpen ? 1001 : undefined,
    width: '100%',
  };
}

interface CustomerSelectFieldProps {
  loading: boolean;
  isEditMode: boolean;
  plantCustomerName: string;
  isDropdownOpen: boolean;
  customerSearchText: string;
  selectedCustomer: CustomerLite | null;
  filteredCustomers: CustomerLite[];
  onToggleDropdown: () => void;
  onSearchChange: (value: string) => void;
  onOpenDropdownFromKeyboard: () => void;
  onSelectCustomer: (customer: CustomerLite) => void;
}

export const CustomerSelectField: React.FC<CustomerSelectFieldProps> = ({
  loading,
  isEditMode,
  plantCustomerName,
  isDropdownOpen,
  customerSearchText,
  selectedCustomer,
  filteredCustomers,
  onToggleDropdown,
  onSearchChange,
  onOpenDropdownFromKeyboard,
  onSelectCustomer,
}) => {
  if (loading) {
    return <div>Loading...</div>;
  }

  if (isEditMode) {
    return (
      <input
        id="plant-customer"
        type="text"
        className="form-control"
        value={selectedCustomer?.customerName || plantCustomerName}
        disabled
        style={{ width: '100%' }}
      />
    );
  }

  const displayValue = getCustomerInputDisplayValue(
    isDropdownOpen,
    customerSearchText,
    selectedCustomer
  );

  return (
    <div className="searchable-select-container" style={{ position: 'relative', width: '100%' }}>
      <div
        className="searchable-select-input"
        role="button"
        tabIndex={0}
        onClick={onToggleDropdown}
        onKeyDown={(event) => handleInteractiveKeyDown(event, onToggleDropdown)}
      >
        <input
          id="plant-customer"
          type="text"
          value={displayValue}
          onChange={(event) => onSearchChange(event.target.value)}
          onFocus={() => undefined}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              onOpenDropdownFromKeyboard();
            }
          }}
          placeholder={isDropdownOpen ? 'Search customers...' : 'Select Customer'}
          className="form-control searchable-input"
          readOnly={!isDropdownOpen}
          disabled={loading}
          style={getSearchableInputStyle({ isOpen: isDropdownOpen })}
        />
        <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} style={dropdownArrowStyle(isDropdownOpen)}>
          <DropdownChevron />
        </span>
      </div>
      {isDropdownOpen && (
        <div className="searchable-dropdown" style={dropdownListStyle} role="listbox">
          {filteredCustomers.map((customer, index) => {
            const label = formatCustomerLabel(customer);
            const isSelected = selectedCustomer?.customerId === customer.customerId;

            return (
              <div
                key={customer.id || `${customer.customerId}-${index}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={`dropdown-option ${isSelected ? 'selected1' : ''}`}
                onClick={() => onSelectCustomer(customer)}
                onKeyDown={(event) => handleInteractiveKeyDown(event, () => onSelectCustomer(customer))}
                style={{
                  padding: '10px 18px',
                  color: '#f8fafc',
                  cursor: 'pointer',
                  fontSize: '14px',
                  backgroundColor: isSelected ? 'rgba(96, 191, 218, 0.16)' : 'transparent',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(event) => {
                  if (!isSelected) {
                    event.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.12)';
                  }
                }}
                onMouseLeave={(event) => {
                  if (!isSelected) {
                    event.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {label}
              </div>
            );
          })}
          {filteredCustomers.length === 0 && (
            <div style={{ padding: '10px 18px', color: '#94a3b8', fontSize: '14px' }}>
              No customers found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface SiteSelectFieldProps {
  loading: boolean;
  isEditMode: boolean;
  siteValue: string;
  isDropdownOpen: boolean;
  siteSearchText: string;
  selectedCustomer: CustomerLite | null;
  filteredSites: Site[];
  onToggleDropdown: () => void;
  onSearchChange: (value: string) => void;
  onOpenDropdownFromKeyboard: () => void;
  onSelectSite: (site: Site) => void;
}

export const SiteSelectField: React.FC<SiteSelectFieldProps> = ({
  loading,
  isEditMode,
  siteValue,
  isDropdownOpen,
  siteSearchText,
  selectedCustomer,
  filteredSites,
  onToggleDropdown,
  onSearchChange,
  onOpenDropdownFromKeyboard,
  onSelectSite,
}) => {
  if (isEditMode) {
    return (
      <input
        id="plant-site"
        type="text"
        className="form-control"
        value={siteValue}
        disabled
        style={{ width: '100%' }}
      />
    );
  }

  const displayValue = isDropdownOpen ? siteSearchText : siteValue;

  return (
    <div className="searchable-select-container" style={{ position: 'relative', width: '100%' }}>
      <div
        className="searchable-select-input"
        role="button"
        tabIndex={0}
        onClick={() => {
          if (selectedCustomer) {
            onToggleDropdown();
          }
        }}
        onKeyDown={(event) => {
          if (selectedCustomer) {
            handleInteractiveKeyDown(event, onToggleDropdown);
          }
        }}
      >
        <input
          id="plant-site"
          type="text"
          value={displayValue}
          onChange={(event) => onSearchChange(event.target.value)}
          onFocus={() => undefined}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              onOpenDropdownFromKeyboard();
            }
          }}
          placeholder={isDropdownOpen ? 'Search sites...' : 'Select Site'}
          className="form-control searchable-input"
          readOnly={!isDropdownOpen}
          disabled={loading || !selectedCustomer}
          style={getSearchableInputStyle({ isOpen: isDropdownOpen })}
        />
        <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} style={dropdownArrowStyle(isDropdownOpen)}>
          <DropdownChevron />
        </span>
      </div>
      {isDropdownOpen && selectedCustomer && (
        <div className="searchable-dropdown" style={dropdownListStyle} role="listbox">
          {filteredSites.map((site, index) => {
            const isSelected = siteValue === site.site;

            return (
              <div
                key={`${site.siteId}-${index}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={`dropdown-option ${isSelected ? 'selected1' : ''}`}
                onClick={() => onSelectSite(site)}
                onKeyDown={(event) => handleInteractiveKeyDown(event, () => onSelectSite(site))}
                style={{
                  padding: '10px 18px',
                  color: '#f8fafc',
                  cursor: 'pointer',
                  fontSize: '14px',
                  backgroundColor: isSelected ? 'rgba(96, 191, 218, 0.16)' : 'transparent',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(event) => {
                  if (!isSelected) {
                    event.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.12)';
                  }
                }}
                onMouseLeave={(event) => {
                  if (!isSelected) {
                    event.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {site.site}
              </div>
            );
          })}
          {filteredSites.length === 0 && (
            <div style={{ padding: '10px 18px', color: '#94a3b8', fontSize: '14px' }}>
              No sites found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface CreatePlantTabsProps {
  currentStep: number;
  mode: 'create' | 'edit';
  plantId?: string;
  assetsCount: number;
  onTabNavigation: (tabNumber: number) => void;
}

function buildPlantTabClassName(
  currentStep: number,
  step: number,
  modifierClass: string,
): string {
  const activeClass = currentStep === step ? 'active' : '';
  return ['tab', activeClass, modifierClass].filter(Boolean).join(' ');
}

function getEditModeTabClick(
  isEditMode: boolean,
  onTabNavigation: (tabNumber: number) => void,
  step: number,
): (() => void) | undefined {
  if (!isEditMode) {
    return undefined;
  }
  return () => onTabNavigation(step);
}

function getNotificationTabModifier(isEditMode: boolean, assetsCount: number): string {
  if (isEditMode && assetsCount > 0) {
    return 'clickable';
  }
  return 'disabled';
}

function getNotificationTabClick(
  isEditMode: boolean,
  assetsCount: number,
  onTabNavigation: (tabNumber: number) => void,
): (() => void) | undefined {
  if (!isEditMode || assetsCount <= 0) {
    return undefined;
  }
  return () => onTabNavigation(3);
}

interface PlantTabProps {
  step: number;
  currentStep: number;
  modifierClass: string;
  label: string;
  onClick?: () => void;
}

const PlantTab: React.FC<PlantTabProps> = ({
  step,
  currentStep,
  modifierClass,
  label,
  onClick,
}) => (
  <div
    className={buildPlantTabClassName(currentStep, step, modifierClass)}
    onClick={onClick}
  >
    <span className="tab-number">{step}</span>
    {' '}
    {label}
  </div>
);

export const CreatePlantTabs: React.FC<CreatePlantTabsProps> = ({
  currentStep,
  mode,
  plantId,
  assetsCount,
  onTabNavigation,
}) => {
  const isEditMode = mode === 'edit';
  const stepOneLabel = plantId ? 'Edit Plant Details' : 'Enter Plant Details';
  const editClickableClass = isEditMode ? 'clickable' : '';

  return (
    <div className="create-plant-tabs">
      <PlantTab
        step={1}
        currentStep={currentStep}
        modifierClass={editClickableClass}
        label={stepOneLabel}
        onClick={getEditModeTabClick(isEditMode, onTabNavigation, 1)}
      />
      <PlantTab
        step={2}
        currentStep={currentStep}
        modifierClass={editClickableClass}
        label="Build Asset Hierarchy"
        onClick={getEditModeTabClick(isEditMode, onTabNavigation, 2)}
      />
      <PlantTab
        step={3}
        currentStep={currentStep}
        modifierClass={getNotificationTabModifier(isEditMode, assetsCount)}
        label="Notification Setup"
        onClick={getNotificationTabClick(isEditMode, assetsCount, onTabNavigation)}
      />
    </div>
  );
};
